/**
 * Abertura do caixa de validação: dá nome ao ponto de portaria que este
 * aparelho representa, logo depois do login e antes de escolher o evento.
 *
 * É também onde o operador escolhe entre o tema noite e o tema dia. O momento é
 * de propósito: ele já está com o aparelho na mão, antes da fila, e é aqui que
 * dá para perceber se o sol vai bater na tela a noite inteira ou não.
 */

import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {ArrowRight, LogOut, Moon, Sun} from 'lucide-react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import {abrirCaixa} from '../caixa';
import {totalPendentesNoAparelho} from '../dados/repositorio';
import {carregarSessao, encerrarSessao} from '../sessao';
import {BotaoIdioma} from '../ui/BotaoIdioma';
import {txt, useIdioma} from '../ui/idioma';
import {alternarModo} from '../ui/modo';
import {estilos, fontes, useTema} from '../ui/tema';
import type {ParamsPilha} from '../navegacao';

type Props = NativeStackScreenProps<ParamsPilha, 'Caixa'>;

/**
 * Sugestões para não obrigar a digitar no meio da correria.
 *
 * O nome do posto é gravado como o operador o vê: quem abriu em espanhol
 * fecha o caixa com "Acceso Principal" no relatório.
 */
function sugestoes(): string[] {
  return [txt('Portaria Principal'), txt('Entrada VIP'), txt('Camarote'), txt('Lateral')];
}

/** Iniciais do operador para o avatar do cabeçalho. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) {
    return '--';
  }
  const primeira = partes[0][0];
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
  return (primeira + ultima).toUpperCase();
}

export function TelaAberturaCaixa({navigation}: Props) {
  const t = useTema();
  useIdioma();

  const [nome, setNome] = useState('');
  const [operador, setOperador] = useState('');
  const [usuarioId, setUsuarioId] = useState('');
  const [focado, setFocado] = useState(false);
  const [abrindo, setAbrindo] = useState(false);

  useEffect(() => {
    carregarSessao().then(sessao => {
      if (!sessao) {
        navigation.replace('Login');
        return;
      }
      setOperador(sessao.nome);
      setUsuarioId(sessao.usuarioId);
    });
  }, [navigation]);

  const podeAbrir = nome.trim().length > 0 && !abrindo && usuarioId.length > 0;

  async function abrir() {
    setAbrindo(true);
    try {
      await abrirCaixa(nome, usuarioId);
      navigation.replace('Eventos');
    } finally {
      setAbrindo(false);
    }
  }

  async function sair() {
    await encerrarSessao();
    navigation.replace('Login');
  }

  /**
   * Trocar de operador apaga o catálogo baixado, para o próximo não herdar os
   * eventos deste. As validações ficam — mas validação pendente só sobe quando
   * alguém abre aquele evento de novo, e o evento pode não estar na lista do
   * operador seguinte. Por isso o aviso: a decisão de sair assim mesmo é de
   * quem está na portaria, não do app, mas ela precisa ser informada.
   */
  async function trocarOperador() {
    const pendentes = await totalPendentesNoAparelho();
    if (pendentes === 0) {
      await sair();
      return;
    }
    const quantas =
      pendentes === 1
        ? txt('{quantas} validação deste aparelho ainda não subiu para o servidor.', {
            quantas: pendentes,
          })
        : txt('{quantas} validações deste aparelho ainda não subiram para o servidor.', {
            quantas: pendentes,
          });
    Alert.alert(
      txt('Validações não enviadas'),
      `${quantas}\n\n` +
        txt(
          'Ao trocar de operador a lista de eventos é apagada, e elas só sobem ' +
            'quando alguém abrir o mesmo evento de novo. Se houver rede, entre no ' +
            'evento e espere a sincronização antes de sair.',
        ),
      [
        {text: txt('Cancelar'), style: 'cancel'},
        {text: txt('Sair mesmo assim'), style: 'destructive', onPress: () => void sair()},
      ],
    );
  }

  const IconeTema = t.modo === 'noite' ? Sun : Moon;

  return (
    <SafeAreaView style={[estilos.tela, {backgroundColor: t.fundo}]} edges={['top', 'bottom']}>
      <View style={[estilos.cabecalho, {borderBottomColor: t.superficie}]}>
        <View
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            backgroundColor: t.primaria,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Text style={{color: t.tintaPrimaria, fontSize: 14, fontFamily: fontes.extra}}>
            {iniciais(operador)}
          </Text>
        </View>

        <View style={{flex: 1}}>
          <Text style={[estilos.tituloCabecalho, {color: t.texto}]} numberOfLines={1}>
            {operador || txt('Carregando...')}
          </Text>
          <Text
            style={{
              color: t.textoApagado,
              fontSize: 12,
              fontFamily: fontes.regular,
              fontVariant: ['tabular-nums'],
            }}>
            {txt('operador')} {usuarioId}
          </Text>
        </View>

        <Pressable
          onPress={alternarModo}
          hitSlop={6}
          accessibilityLabel={
            t.modo === 'noite' ? txt('Mudar para o tema dia') : txt('Mudar para o tema noite')
          }
          style={[estilos.botaoIcone, {borderColor: t.borda}]}>
          <IconeTema size={20} color={t.textoFraco} strokeWidth={2} />
        </Pressable>

        <Pressable
          onPress={trocarOperador}
          hitSlop={6}
          accessibilityLabel={txt('Entrar com outro operador')}
          style={[estilos.botaoIcone, {borderColor: t.borda}]}>
          <LogOut size={20} color={t.textoFraco} strokeWidth={2} />
        </Pressable>
      </View>

      <KeyboardAvoidingView
        style={{flex: 1}}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          contentContainerStyle={{padding: 24, gap: 20, flexGrow: 1}}
          keyboardShouldPersistTaps="handled">
          <View style={{gap: 8}}>
            <Text style={[estilos.titulo, {color: t.texto}]}>{txt('Que posto é este aparelho?')}</Text>
            <Text style={[estilos.subtitulo, {color: t.textoFraco}]}>
              {txt('O nome aparece no topo do validador e no fechamento. Fica só neste aparelho.')}
            </Text>
          </View>

          <TextInput
            style={[
              estilos.campo,
              {
                backgroundColor: t.superficie,
                borderColor: focado ? t.primaria : t.borda,
                borderWidth: focado ? 2 : 1,
                color: t.texto,
                fontSize: 22,
                fontFamily: fontes.negrito,
              },
            ]}
            value={nome}
            onChangeText={setNome}
            onFocus={() => setFocado(true)}
            onBlur={() => setFocado(false)}
            placeholder={txt('Ex.: Portaria Principal')}
            placeholderTextColor={t.textoApagado}
            autoCapitalize="words"
            autoCorrect={false}
            returnKeyType="go"
            onSubmitEditing={() => podeAbrir && abrir()}
          />

          <View style={{gap: 10}}>
            <Text style={[estilos.rotulo, {color: t.textoFraco}]}>{txt('Sugestões')}</Text>
            <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 9}}>
              {sugestoes().map(sugestao => {
                const escolhida = nome.trim() === sugestao;
                return (
                  <Pressable
                    key={sugestao}
                    onPress={() => setNome(sugestao)}
                    style={{
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: escolhida ? t.sucessoFundo : t.superficie,
                      borderWidth: 1,
                      borderColor: escolhida ? t.primaria : t.borda,
                    }}>
                    <Text
                      style={{
                        color: escolhida ? t.destaque : t.textoMedio,
                        fontSize: 15,
                        fontFamily: fontes.semi,
                      }}>
                      {sugestao}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      <View style={{padding: 24, paddingTop: 12, gap: 14}}>
        <BotaoIdioma />

        <Pressable
          onPress={abrir}
          disabled={!podeAbrir}
          style={[estilos.botao, {backgroundColor: podeAbrir ? t.primaria : t.superficieAlta}]}>
          {abrindo ? (
            <ActivityIndicator color={t.tintaPrimaria} />
          ) : (
            <>
              <Text
                style={[
                  estilos.textoBotao,
                  {color: podeAbrir ? t.tintaPrimaria : t.textoApagado},
                ]}>
                {txt('Abrir')}
              </Text>
              <ArrowRight
                size={20}
                color={podeAbrir ? t.tintaPrimaria : t.textoApagado}
                strokeWidth={2.6}
              />
            </>
          )}
        </Pressable>
      </View>
    </SafeAreaView>
  );
}
